import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Alert, Typography, Space, Tag, Table, Modal, Form, Input, InputNumber, Switch, Upload, Tabs, Image, Select, Divider, List, Avatar, Progress, message } from 'antd';
import { 
    FileTextOutlined, 
    PictureOutlined, 
    SettingOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    SaveOutlined,
    UploadOutlined,
    FolderOutlined,
    FileImageOutlined,
    VideoCameraOutlined,
    FileOutlined,
    SearchOutlined,
    FilterOutlined,
    GlobalOutlined,
    EditFilled,
    DeleteFilled,
    CheckCircleOutlined
} from '@ant-design/icons';
import api from '../../../services/api';
import { useHomepageContent, useFooterContent, useAboutContent, useServicesContent, getContentByKey } from '../../../hooks/useDynamicContent';

const { Title, Paragraph, Text } = Typography;

function ContentDemo() {
    const [activeTab, setActiveTab] = useState('preview');
    
    // Load dynamic content
    const { content: homepageContent, loading: homepageLoading } = useHomepageContent();
    const { content: footerContent, loading: footerLoading } = useFooterContent();
    const { content: aboutContent, loading: aboutLoading } = useAboutContent();
    const { content: servicesContent, loading: servicesLoading } = useServicesContent();

    const contentSections = [
        {
            key: 'homepage',
            title: 'Homepage Content',
            content: homepageContent,
            loading: homepageLoading,
            items: [
                { key: 'homepage_hero_title', label: 'Hero Title' },
                { key: 'homepage_hero_subtitle', label: 'Hero Subtitle' },
                { key: 'homepage_features_title', label: 'Features Title' },
                { key: 'homepage_features_subtitle', label: 'Features Subtitle' }
            ]
        },
        {
            key: 'footer',
            title: 'Footer Content',
            content: footerContent,
            loading: footerLoading,
            items: [
                { key: 'footer_company_name', label: 'Company Name' },
                { key: 'footer_about_text', label: 'About Text' },
                { key: 'footer_copyright', label: 'Copyright' }
            ]
        },
        {
            key: 'about',
            title: 'About Page Content',
            content: aboutContent,
            loading: aboutLoading,
            items: [
                { key: 'about_title', label: 'Page Title' },
                { key: 'about_description', label: 'Description' }
            ]
        },
        {
            key: 'services',
            title: 'Services Page Content',
            content: servicesContent,
            loading: servicesLoading,
            items: [
                { key: 'services_title', label: 'Page Title' },
                { key: 'services_subtitle', label: 'Subtitle' }
            ]
        }
    ];

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Dynamic Content Demo</Title>
                <Paragraph type="secondary">
                    This page demonstrates how the dynamic content system works. All content below is loaded from the admin panel and can be updated in real-time.
                </Paragraph>
            </div>

            <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab}
                items={[
                    {
                        key: 'preview',
                        label: 'Content Preview',
                        children: (
                    <Row gutter={[16, 16]}>
                        {contentSections.map(section => (
                            <Col xs={24} lg={12} key={section.key}>
                                <Card 
                                    title={section.title}
                                    extra={
                                        <Tag color={section.loading ? 'orange' : 'green'}>
                                            {section.loading ? 'Loading...' : 'Loaded'}
                                        </Tag>
                                    }
                                >
                                    {section.loading ? (
                                        <div className="space-y-3">
                                            {section.items.map(item => (
                                                <div key={item.key}>
                                                    <Text strong>{item.label}:</Text>
                                                    <div className="mt-1">
                                                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {section.items.map(item => (
                                                <div key={item.key}>
                                                    <Text strong>{item.label}:</Text>
                                                    <div className="mt-1 p-2 bg-gray-50 rounded text-sm">
                                                        {getContentByKey(section.content, item.key, 'No content available')}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </Col>
                        ))}
                    </Row>
                        )
                    },
                    {
                        key: 'live',
                        label: 'Live Preview',
                        children: (
                    <Card title="Live Content Preview">
                        <div className="space-y-8">
                            {/* Homepage Hero Preview */}
                            <div className="border rounded-lg p-6 bg-gradient-to-b from-indigo-50 to-white">
                                <Title level={4} className="mb-4">Homepage Hero Section</Title>
                                <div className="space-y-4">
                                    <Title level={1} className="!text-4xl">
                                        {homepageLoading ? (
                                            <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                                        ) : (
                                            getContentByKey(homepageContent, 'homepage_hero_title', 'Default Hero Title')
                                        )}
                                    </Title>
                                    <Paragraph className="!text-lg !text-gray-600">
                                        {homepageLoading ? (
                                            <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                                        ) : (
                                            getContentByKey(homepageContent, 'homepage_hero_subtitle', 'Default hero subtitle')
                                        )}
                                    </Paragraph>
                                </div>
                            </div>

                            {/* Features Section Preview */}
                            <div className="border rounded-lg p-6 bg-gray-50">
                                <Title level={4} className="mb-4">Features Section</Title>
                                <div className="text-center space-y-4">
                                    <Title level={2}>
                                        {homepageLoading ? (
                                            <div className="h-6 bg-gray-200 rounded animate-pulse mx-auto" style={{ width: '60%' }}></div>
                                        ) : (
                                            getContentByKey(homepageContent, 'homepage_features_title', 'Default Features Title')
                                        )}
                                    </Title>
                                    <Paragraph className="!text-gray-600 max-w-2xl mx-auto">
                                        {homepageLoading ? (
                                            <div className="h-5 bg-gray-200 rounded animate-pulse mx-auto" style={{ width: '80%' }}></div>
                                        ) : (
                                            getContentByKey(homepageContent, 'homepage_features_subtitle', 'Default features subtitle')
                                        )}
                                    </Paragraph>
                                </div>
                            </div>

                            {/* Footer Preview */}
                            <div className="border rounded-lg p-6 bg-gray-800 text-gray-100">
                                <Title level={4} className="mb-4 text-gray-100">Footer Section</Title>
                                <div className="space-y-4">
                                    <Title level={3} className="!text-gray-100">
                                        {footerLoading ? (
                                            <div className="h-6 bg-gray-700 rounded animate-pulse" style={{ width: '40%' }}></div>
                                        ) : (
                                            getContentByKey(footerContent, 'footer_company_name', 'Default Company Name')
                                        )}
                                    </Title>
                                    <Paragraph className="!text-gray-300">
                                        {footerLoading ? (
                                            <div className="h-5 bg-gray-700 rounded animate-pulse"></div>
                                        ) : (
                                            getContentByKey(footerContent, 'footer_about_text', 'Default footer about text')
                                        )}
                                    </Paragraph>
                                    <Text className="text-gray-400">
                                        {footerLoading ? (
                                            <div className="h-4 bg-gray-700 rounded animate-pulse" style={{ width: '60%' }}></div>
                                        ) : (
                                            getContentByKey(footerContent, 'footer_copyright', '© 2025 Default Company')
                                        )}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </Card>
                        )
                    },
                    {
                        key: 'how',
                        label: 'How It Works',
                        children: (
                    <Card title="Dynamic Content System">
                        <div className="space-y-6">
                            <div>
                                <Title level={4}>How Dynamic Content Works</Title>
                                <Paragraph>
                                    The dynamic content system allows you to control all website content from the admin panel without touching any code.
                                </Paragraph>
                            </div>

                            <div>
                                <Title level={4}>Key Features</Title>
                                <List
                                    dataSource={[
                                        'Real-time content updates',
                                        'No code changes required',
                                        'SEO-friendly dynamic meta tags',
                                        'Media library management',
                                        'Content versioning and history',
                                        'Multi-language support ready',
                                        'Fallback content for reliability'
                                    ]}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                            {item}
                                        </List.Item>
                                    )}
                                />
                            </div>

                            <div>
                                <Title level={4}>Content Types Supported</Title>
                                <Row gutter={[8, 8]}>
                                    <Col><Tag color="blue">Text Content</Tag></Col>
                                    <Col><Tag color="green">HTML Content</Tag></Col>
                                    <Col><Tag color="purple">Image Content</Tag></Col>
                                    <Col><Tag color="orange">JSON Content</Tag></Col>
                                </Row>
                            </div>

                            <div>
                                <Title level={4}>Getting Started</Title>
                                <ol className="list-decimal list-inside space-y-2">
                                    <li>Go to Admin Panel → Content Management</li>
                                    <li>Create or edit content items</li>
                                    <li>Changes appear instantly on the website</li>
                                    <li>Use the Media Library for images and files</li>
                                    <li>Configure SEO settings for better search rankings</li>
                                </ol>
                            </div>
                        </div>
                    </Card>
                        )
                    }
                ]}
            />
        </div>
    );
}

export default ContentDemo;
